import { useEffect } from 'preact/hooks';
import type { GaugeWidget } from '../widgets';
import type { IPointBindingSource } from '../binding';
import { pointSignal } from '../pointSignal';
import { isDegraded } from '../quality';
import { useLeafMeta } from '../LeafMetaContext';
import { toneFor, zonesFromLeaf, type LimitTone } from '../leafMeta';
import { formatNumber } from '../../format/leafValue';
import './GaugeRenderer.css';

interface Props {
  widget: GaugeWidget;
  source: IPointBindingSource;
}

/**
 * Radial or 240° arc gauge. Renders as SVG for crisp scaling on any panel
 * resolution. Zone bands are drawn under the value arc so the operator sees
 * warn/alarm ranges even when the current value sits deep inside them.
 *
 * Two design choices worth naming:
 *   - The value arc animates via CSS transition on `stroke-dasharray`, not
 *     JS lerp. Cheap, GPU-composited, no rAF loop per gauge.
 *   - Freshness/staleness is signalled by dimming the whole widget (30% alpha)
 *     when the last sample carries a bad/stale quality, echoing the concept
 *     rule from §1 ("Quality und Staleness gehören ins Widget").
 */
export function GaugeRenderer({ widget: authored, source }: Props) {
  // Fill gaps from the leaf (hmi/leafMeta.ts): unit, and zones from the warn
  // and alarm limits when the author set none. What the widget says wins.
  const meta = useLeafMeta(authored.valuePoint);
  const widget: GaugeWidget = {
    ...authored,
    unit: authored.unit ?? meta?.unit ?? undefined,
    zones: authored.zones && authored.zones.length > 0
      ? authored.zones
      : zonesFromLeaf(meta, authored.min, authored.max),
  };
  const { value, release } = pointSignal(source, widget.valuePoint);
  useEffect(() => release, [release]);

  const setpointSig = widget.setpointPoint
    ? pointSignal(source, widget.setpointPoint)
    : null;
  useEffect(() => setpointSig?.release ?? (() => {}), [setpointSig]);

  const current = value.value;
  const numericValue = typeof current?.value === 'number' ? current.value : null;
  const setpoint = typeof setpointSig?.value.value?.value === 'number'
    ? setpointSig.value.value.value
    : null;

  const stale = isDegraded(current);
  const decimals = widget.decimals ?? 1;
  const tone = toneFor(meta, numericValue);

  // Arc geometry. Radial style covers 270°, arc240 covers 240°, bar drops to
  // a horizontal fill. We compute the sweep angle in radians for the trig
  // and produce SVG path strings for arc + zone strokes.
  const style = widget.style ?? 'radial';
  const sweepDeg = style === 'arc240' ? 240 : style === 'bar' ? 0 : 270;
  const startDeg = 90 + (360 - sweepDeg) / 2;
  const size = 160;
  const cx = size / 2;
  const cy = size / 2;
  const r = size * 0.4;
  const strokeWidth = size * 0.08;

  if (style === 'bar') {
    return <GaugeBar widget={widget} numericValue={numericValue} setpoint={setpoint}
                     stale={stale} decimals={decimals} tone={tone} />;
  }

  const arcPath = describeArc(cx, cy, r, startDeg, startDeg + sweepDeg);
  const valueFrac = numericValue != null
    ? clamp((numericValue - widget.min) / (widget.max - widget.min), 0, 1)
    : 0;
  const valueArcDeg = valueFrac * sweepDeg;
  const valueArcPath = describeArc(cx, cy, r, startDeg, startDeg + valueArcDeg);

  const setpointMarkerAngle = setpoint != null
    ? startDeg + clamp((setpoint - widget.min) / (widget.max - widget.min), 0, 1) * sweepDeg
    : null;

  return (
    <div class={`hv-gauge${stale ? ' hv-gauge--stale' : ''}`}>
      <svg class="hv-gauge__svg" viewBox={`0 0 ${size} ${size}`} role="img"
           aria-label={widget.label ?? 'Gauge'}>
        {/* Track */}
        <path class="hv-gauge__track"
              d={arcPath}
              stroke-width={strokeWidth}
              fill="none"
              stroke-linecap="round" />
        {/* Zone bands drawn UNDER the value arc so the current arc paints on
            top and stays legible even when the value is deep in a zone. */}
        {widget.zones?.map((z) => {
          const zoneStartFrac = clamp((z.from - widget.min) / (widget.max - widget.min), 0, 1);
          const zoneEndFrac = clamp((z.to - widget.min) / (widget.max - widget.min), 0, 1);
          const zoneStart = startDeg + zoneStartFrac * sweepDeg;
          const zoneEnd = startDeg + zoneEndFrac * sweepDeg;
          return (
            <path key={`${z.from}-${z.to}`}
                  class={`hv-gauge__zone hv-gauge__zone--${z.tone}`}
                  d={describeArc(cx, cy, r, zoneStart, zoneEnd)}
                  stroke-width={strokeWidth * 0.75}
                  fill="none"
                  stroke-linecap="butt" />
          );
        })}
        {/* Value arc */}
        {numericValue != null && (
          <path class="hv-gauge__value"
                d={valueArcPath}
                stroke-width={strokeWidth}
                fill="none"
                stroke-linecap="round" />
        )}
        {/* Setpoint marker */}
        {setpointMarkerAngle != null && (
          (() => {
            const rad = (setpointMarkerAngle * Math.PI) / 180;
            const outer = r + strokeWidth * 0.6;
            const inner = r - strokeWidth * 0.6;
            return (
              <line class="hv-gauge__setpoint"
                    x1={cx + Math.cos(rad) * inner}
                    y1={cy + Math.sin(rad) * inner}
                    x2={cx + Math.cos(rad) * outer}
                    y2={cy + Math.sin(rad) * outer}
                    stroke-width={2} />
            );
          })()
        )}
      </svg>
      <div class="hv-gauge__readout">
        <span class={`hv-gauge__value-text${tone ? ` hv-gauge__value-text--${tone}` : ''}`}>
          {numericValue != null ? formatNumber(numericValue, decimals) : '—'}
        </span>
        {widget.unit && (
          <span class="hv-gauge__unit">{widget.unit}</span>
        )}
      </div>
      {widget.label && <div class="hv-gauge__label">{widget.label}</div>}
    </div>
  );
}

function GaugeBar({ widget, numericValue, setpoint, stale, decimals, tone }: {
  widget: GaugeWidget;
  numericValue: number | null;
  setpoint: number | null;
  stale: boolean;
  decimals: number;
  tone: LimitTone | null;
}) {
  const valueFrac = numericValue != null
    ? clamp((numericValue - widget.min) / (widget.max - widget.min), 0, 1)
    : 0;
  const setpointFrac = setpoint != null
    ? clamp((setpoint - widget.min) / (widget.max - widget.min), 0, 1)
    : null;
  return (
    <div class={`hv-gauge hv-gauge--bar${stale ? ' hv-gauge--stale' : ''}`}>
      <div class="hv-gauge__bar-track">
        {widget.zones?.map((z) => {
          const from = clamp((z.from - widget.min) / (widget.max - widget.min), 0, 1);
          const to = clamp((z.to - widget.min) / (widget.max - widget.min), 0, 1);
          return (
            <span key={`${z.from}-${z.to}`}
                  class={`hv-gauge__bar-zone hv-gauge__bar-zone--${z.tone}`}
                  style={{ left: `${from * 100}%`, width: `${(to - from) * 100}%` }} />
          );
        })}
        <span class="hv-gauge__bar-fill" style={{ width: `${valueFrac * 100}%` }} />
        {setpointFrac != null && (
          <span class="hv-gauge__bar-setpoint" style={{ left: `${setpointFrac * 100}%` }} />
        )}
      </div>
      <div class="hv-gauge__readout hv-gauge__readout--bar">
        <span class={`hv-gauge__value-text${tone ? ` hv-gauge__value-text--${tone}` : ''}`}>
          {numericValue != null ? formatNumber(numericValue, decimals) : '—'}
        </span>
        {widget.unit && <span class="hv-gauge__unit">{widget.unit}</span>}
      </div>
      {widget.label && <div class="hv-gauge__label">{widget.label}</div>}
    </div>
  );
}


function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** SVG arc path from `startDeg` to `endDeg` clockwise on a circle centred at
 *  (cx, cy) with radius r. Angles are measured clockwise from 3 o'clock in
 *  degrees. Handles the case where the endpoints coincide (arc reduces to a
 *  degenerate curve; we emit an M-only path so nothing renders). */
function describeArc(cx: number, cy: number, r: number, startDeg: number, endDeg: number): string {
  if (Math.abs(endDeg - startDeg) < 0.01) return `M ${cx} ${cy}`;
  const startRad = (startDeg * Math.PI) / 180;
  const endRad = (endDeg * Math.PI) / 180;
  const startX = cx + Math.cos(startRad) * r;
  const startY = cy + Math.sin(startRad) * r;
  const endX = cx + Math.cos(endRad) * r;
  const endY = cy + Math.sin(endRad) * r;
  const largeArcFlag = endDeg - startDeg <= 180 ? 0 : 1;
  return `M ${startX} ${startY} A ${r} ${r} 0 ${largeArcFlag} 1 ${endX} ${endY}`;
}
